// Per-campaign breakdown table (FF-28 / MIS-1011).
//
// Renders the REAL `GET /api/analytics/by-campaign` rows (FF-12; apps/worker/
// src/routes/analytics.ts + db/repos/analytics.ts) in the shared `DataTable`,
// mirroring AttributionTable. Each row is keyed on the FB-dynamic
// `fb_campaign_id` and shaped exactly:
//   { campaignId, campaignName, visits, leads, calls, conversions,
//     revenueCents, spendCents, roas }
// Derived columns are render-edge only:
//   • ROAS comes PRE-COMPUTED from the server (`roas: number | null`, null when
//     spend is 0) — rendered as "2.5x", '—' when null.
//   • CPA = spendCents / conversions, '—' at 0 conversions (never divide by 0).
// Cents→dollars conversion happens here at the render edge only (formatCurrency);
// all values are aggregates — no individual lead PII is ever shown.
//
// Sorting: same tiny owned sort state as AttributionTable (the shared DataTable
// doesn't wire TanStack's sorted row model) — numeric columns click-to-sort.

import type { ColumnDef } from '@tanstack/react-table';
import type { InferResponseType } from 'hono/client';
import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/ui/DataTable';
import { formatCount, formatCurrency } from '@/lib/analytics';
import type { ApiClient } from '@/lib/analytics-types';
import { cn } from '@/lib/utils';

// `InferResponseType` yields the UNION of every status response (200 success +
// the 400 `{ error, issues }` validation body); narrow to the success member by
// the discriminating `rows` key before indexing a single campaign row.
type ByCampaignResponse = InferResponseType<ApiClient['api']['analytics']['by-campaign']['$get']>;
type ByCampaignSuccess = Extract<ByCampaignResponse, { rows: unknown }>;
export type ByCampaignRow = ByCampaignSuccess['rows'][number];

/** Numeric columns that support click-to-sort. */
type SortKey = 'visits' | 'leads' | 'calls' | 'conversions' | 'revenueCents' | 'spendCents';
type SortDir = 'asc' | 'desc';

interface SortableHeaderProps {
  label: string;
  column: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (column: SortKey) => void;
}

function SortableHeader({
  label,
  column,
  active,
  dir,
  onSort,
}: SortableHeaderProps): React.ReactElement {
  const isActive = active === column;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={cn(
        'inline-flex items-center gap-1 font-medium transition-colors hover:text-gray-900',
        isActive ? 'text-gray-900' : 'text-gray-600',
      )}
    >
      {label}
      <span aria-hidden className="text-[10px] leading-none">
        {isActive ? (dir === 'desc' ? '▼' : '▲') : '↕'}
      </span>
    </button>
  );
}

/** ROAS render rule: server sends null when spend is 0 → '—'; else "2.5x". */
export function formatRoas(roas: number | null): string {
  return roas === null ? '—' : `${roas.toFixed(1)}x`;
}

/** CPA = spend / conversions, '—' at 0 conversions (ROAS-style undefined). */
export function formatCpa(spendCents: number, conversions: number): string {
  if (conversions <= 0) return '—';
  return formatCurrency(spendCents / conversions);
}

export function CampaignsTable({ rows }: { rows: ByCampaignRow[] }): React.ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>('visits');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Stable identity so the columns memo can depend on it without rebuilding
  // every render. Uses functional setState updaters → no extra deps needed.
  const onSort = useCallback((column: SortKey): void => {
    setSortKey((prevKey) => {
      if (prevKey === column) {
        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        return prevKey;
      }
      setSortDir('desc');
      return column;
    });
  }, []);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const diff = a[sortKey] - b[sortKey];
      return sortDir === 'desc' ? -diff : diff;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const columns = useMemo<ColumnDef<ByCampaignRow, unknown>[]>(() => {
    const numCol = (
      key: SortKey,
      label: string,
      format: (n: number) => string,
    ): ColumnDef<ByCampaignRow, unknown> => ({
      accessorKey: key,
      header: () => (
        <SortableHeader label={label} column={key} active={sortKey} dir={sortDir} onSort={onSort} />
      ),
      cell: ({ row }) => <span className="tabular-nums">{format(row.original[key])}</span>,
    });

    return [
      {
        accessorKey: 'campaignId',
        header: 'Campaign',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900">
              {row.original.campaignName ?? '(unnamed)'}
            </div>
            <div className="text-xs text-gray-500">{row.original.campaignId}</div>
          </div>
        ),
      },
      numCol('visits', 'Visits', formatCount),
      numCol('leads', 'Leads', formatCount),
      numCol('calls', 'Calls', formatCount),
      numCol('conversions', 'Conv', formatCount),
      numCol('revenueCents', 'Revenue', formatCurrency),
      numCol('spendCents', 'Spend', formatCurrency),
      {
        id: 'roas',
        header: 'ROAS',
        cell: ({ row }) => <span className="tabular-nums">{formatRoas(row.original.roas)}</span>,
      },
      {
        id: 'cpa',
        header: 'CPA',
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatCpa(row.original.spendCents, row.original.conversions)}
          </span>
        ),
      },
    ];
  }, [sortKey, sortDir, onSort]);

  return (
    <DataTable
      columns={columns}
      data={sorted}
      pageSize={20}
      emptyMessage="No campaign data for this range yet."
    />
  );
}
